import React from "react";
import { BedDouble, UtensilsCrossed, Sparkles, ArrowRight, RefreshCw, Ship } from "lucide-react";
import { useCruise } from "@/context/CruiseContext";
import { useShipReference, useBookedDining } from "./useShipReference";
import { matchCabinCategory, formatReservationDate } from "./shipRefUtils";

// RC8 · Compact "Explore <ship>" module for Voyage Home (desktop CruiseHome +
// mobile MobileToday). Personalised and concise — the complete Widgety
// catalogue lives in the dedicated Ship Guide (opened via the CTA). This module
// NEVER renders the full cabin / dining / venue card lists.

function nextUpcomingReservation(reservations) {
  const today = new Date().toISOString().slice(0, 10);
  const dated = (reservations || []).filter((r) => r?.date);
  const upcoming = dated.filter((r) => r.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  return upcoming[0] || dated.sort((a, b) => a.date.localeCompare(b.date))[0] || null;
}

const Stat = ({ icon: Icon, value, label, testId }) => (
  <div className="flex items-center gap-2" data-testid={testId}>
    <Icon className="w-4 h-4 text-[#0EA5E9]" />
    <span className="text-[15px] font-bold text-[#0F172A] tabular-nums">{value}</span>
    <span className="text-[12px] text-[#64748B]">{label}</span>
  </div>
);

export const ExploreShipModule = ({ shipId, shipName, onOpenGuide }) => {
  const { voyageMeta } = useCruise();
  const ref = useShipReference(shipId, shipName);
  const { reservations } = useBookedDining();

  // Do not render when there is no ship, or when the caller is not licensed
  // (401/403). Never leak licensed content.
  if (!ref.sid) return null;
  if (ref.denied) return null;

  // Recoverable failure — keep the module visible with a Retry affordance.
  if (ref.error) {
    return (
      <section className="cc-card p-5" data-testid="explore-ship-module">
        <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="explore-ship-error">
          <p className="text-[13px] text-[#64748B]">We couldn’t load ship reference data just now.</p>
          <button
            type="button"
            onClick={ref.reload}
            data-testid="explore-ship-retry"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#0EA5E9] px-4 py-1.5 text-[12.5px] font-semibold text-white hover:brightness-95"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Try again
          </button>
        </div>
      </section>
    );
  }

  const cabinNumber = voyageMeta?.cabin_number || voyageMeta?.stateroom || null;
  const cabinCategory = voyageMeta?.cabin_category || null;
  const matched = matchCabinCategory(voyageMeta?.cabin_category_id, ref.cabins);
  const nextRes = nextUpcomingReservation(reservations);
  const isWidgety = ref.meta.source === "widgety";

  return (
    <section className="cc-card p-5" data-testid="explore-ship-module">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-[#0F2244] text-white flex items-center justify-center shrink-0">
            <Ship className="w-4 h-4" />
          </div>
          <div>
            <div className="cc-overline text-[#94A3B8]">Explore</div>
            <h3 className="text-lg font-bold text-[#0F172A] leading-tight" data-testid="explore-ship-title">
              {shipName || ref.meta.ship_name || "Your ship"}
            </h3>
          </div>
        </div>
        {isWidgety && (
          <div className="text-[11px] text-[#64748B] text-right pt-1" data-testid="explore-ship-attribution">
            Data provided by <span className="font-semibold text-[#0F172A]">Widgety</span>
          </div>
        )}
      </div>

      {/* Personalised: Your Stateroom + Your Dining */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        {/* Your Stateroom */}
        <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3.5" data-testid="explore-ship-stateroom">
          <div className="cc-overline text-[#94A3B8] mb-1.5 flex items-center gap-1.5">
            <BedDouble className="w-3.5 h-3.5" /> Your stateroom
          </div>
          {cabinNumber || cabinCategory ? (
            <>
              {cabinNumber && (
                <div className="text-[15px] font-bold text-[#0F172A]" data-testid="explore-ship-cabin-number">
                  Cabin {cabinNumber}
                </div>
              )}
              {cabinCategory && (
                <div className="text-[12.5px] text-[#475569]" data-testid="explore-ship-cabin-category">
                  {cabinCategory}
                </div>
              )}
              {matched ? (
                <button
                  type="button"
                  onClick={() => onOpenGuide()}
                  data-testid="explore-ship-cabin-match-link"
                  className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-semibold text-[#0EA5E9] hover:underline"
                >
                  View category details <ArrowRight className="w-3 h-3" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => onOpenGuide("cabins")}
                  data-testid="explore-ship-cabin-explore-btn"
                  className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-semibold text-[#0EA5E9] hover:underline"
                >
                  Explore cabin categories <ArrowRight className="w-3 h-3" />
                </button>
              )}
            </>
          ) : (
            <div className="text-[12.5px] text-[#94A3B8]">Cabin assigned at check-in</div>
          )}
        </div>

        {/* Your Dining */}
        <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3.5" data-testid="explore-ship-dining">
          <div className="cc-overline text-[#94A3B8] mb-1.5 flex items-center gap-1.5">
            <UtensilsCrossed className="w-3.5 h-3.5" /> Your dining
          </div>
          {reservations.length > 0 ? (
            <>
              <div className="text-[15px] font-bold text-[#0F172A]" data-testid="explore-ship-dining-count">
                {reservations.length} reservation{reservations.length === 1 ? "" : "s"}
              </div>
              {nextRes && (
                <div className="text-[12.5px] text-[#475569]" data-testid="explore-ship-dining-next">
                  Next · {nextRes.venue_name}
                  {nextRes.date ? ` · ${formatReservationDate(nextRes.date)}` : ""}
                  {nextRes.time ? ` ${nextRes.time}` : ""}
                </div>
              )}
            </>
          ) : (
            <div className="text-[12.5px] text-[#94A3B8]">No dining reservations yet</div>
          )}
        </div>
      </div>

      {/* Ship-reference totals */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-4" data-testid="explore-ship-totals">
        <Stat icon={BedDouble} value={ref.cabins.length} label="cabin categories" testId="explore-ship-total-cabins" />
        <Stat icon={UtensilsCrossed} value={ref.dining.length} label="dining options" testId="explore-ship-total-dining" />
        <Stat icon={Sparkles} value={ref.venues.length} label="venues & entertainment" testId="explore-ship-total-venues" />
      </div>

      {/* Primary CTA */}
      <button
        type="button"
        onClick={() => onOpenGuide()}
        data-testid="explore-ship-open-guide"
        className="inline-flex items-center gap-2 rounded-full bg-[#0F2244] px-5 py-2.5 text-[13px] font-semibold text-white hover:bg-[#14315e] transition-colors"
      >
        Open Ship Guide <ArrowRight className="w-4 h-4" />
      </button>
    </section>
  );
};

export default ExploreShipModule;
